import { autoinject } from 'aurelia-framework';
import { HttpClient, json } from 'aurelia-fetch-client';
import { MessagingProxy } from './cap-proxy';
import { Remote, XrmContext } from './shared-crm-proxy-types';
import { Router } from 'aurelia-router';
import { defaultTimeout, timeout } from './timeout';

export type customerStatus = "loading" | "notregistered" | "registered";

export interface CustomerInfo {

    id: string | null;
    company: string | null
    name: string | null
    email: string | null
    contractLines: ContractLine[];
    status: customerStatus;

}
export interface CustomerRegistrationInfo extends CustomerInfo {
    gdprConsentGiven: boolean;
    signedUpForNewsLetter: boolean;
    organizations: Organization[];
}

export interface ContractLine {
    contractLineId: string;
    name: string;
    quantity: number;
    usageQuery: string;
    usage: number;
    usageByEnvironment: number;
    isExceeded: boolean;
    usageBundle: UsageBundle;
    licensesUsedPercentage: number;
    status: StatusEnum;
    features: Feature[];
}

export interface Feature {
    id: number;
    name: string;
}

export interface UsageBundle {
    startDate: string;
    startDateFormatted: string;
    endDate: string;
    endDateFormatted: string;
    current: number;
    forecast: number;
    total: number;
    transferred: number;
    transferredPreviousBundle: number;
    usedPercentage: number;
    periodPercentage: number;
    plannedUsedToday: number;
}

export enum StatusEnum {
    Ok = "ok",
    Warn = "warn",
    Error = "error",
}

interface Organization {
    id: string;
    uniqueName: string;
    isProduction: boolean;
}

export enum RegisterAction {
    Register = "Register",
    Update = "Update"
}
interface RegistrationInfo {
    isProduction: boolean;
    organizationName: string;
    organizationId: string;
}
export interface RegisterCustomerInputModel extends RegistrationInfo {
    id: string;
    company: string;
    name: string;
    email: string;
    gdprConsentGiven: boolean;
    signedUpForNewsLetter: boolean;
    registerAction: RegisterAction;
}
interface TokenRegistrationInfo extends RegistrationInfo {
    token: string;
}

@autoinject
export class CustomerInfoStore {

    private static readonly DefaultPlatformType: number = 250000000; // D365CE

    private cache: Map<string, CustomerRegistrationInfo> = new Map<string, CustomerRegistrationInfo>();

    /**
     *
     */
    constructor(private http: HttpClient, private router: Router, private proxy: MessagingProxy<Remote>) {

        http.configure(config => {
            config
                .useStandardConfiguration();
        });

    }

    getEnvironmentUrl(): Promise<string> {
        return timeout(defaultTimeout,
            this.proxy.invoke("getXrmContext")
        ).then((context: XrmContext) => {

            return context.clientUrl;
        });
    }

    getCustomerInfo(organizationId: string, contractLineId: string): CustomerRegistrationInfo {
        let customerInfo = this.cache.get(organizationId);
        if (customerInfo === undefined) {
            customerInfo = {
                id: null,
                company: null,
                name: null,
                email: null,
                contractLines: [],
                organizations: [],
                gdprConsentGiven: false,
                signedUpForNewsLetter: false,
                status: "loading"
            };
            this.cache.set(organizationId, customerInfo);
            this.retrieveOrganization(organizationId)
                .then(r => {
                    // If contractline id is provided, filter the list of contract lines
                    if (contractLineId) {
                        r.contractLines = r.contractLines.filter(cl => cl.contractLineId === contractLineId);
                    }

                    this.updateCustomer(organizationId, r);
                });
        }

        return customerInfo;
    }


    async activateByToken(tokenRegistrationInfo: TokenRegistrationInfo) {
        const environmentUrl = await this.getEnvironmentUrl();

        const record = {
            Id: tokenRegistrationInfo.organizationId,
            UniqueName: tokenRegistrationInfo.organizationName,
            Token: tokenRegistrationInfo.token,
            IsProduction: tokenRegistrationInfo.isProduction,
            EnvironmentUrl: environmentUrl,
            PlatformType: CustomerInfoStore.DefaultPlatformType
        };

        await this.registerOrganization(tokenRegistrationInfo.organizationId, "/api/organization/activate", record, "activating token");
    }


    async register(customerRegistrationInfo: RegisterCustomerInputModel) {
        // Get the client url from the Xrm context
        const environmentUrl = await this.getEnvironmentUrl();

        const record = {
            Id: customerRegistrationInfo.id,
            Company: customerRegistrationInfo.company,
            Name: customerRegistrationInfo.name,
            Email: customerRegistrationInfo.email,
            GdprConsentGiven: customerRegistrationInfo.gdprConsentGiven,
            SignedUpForNewsLetter: customerRegistrationInfo.signedUpForNewsLetter,
            Organizations: [
                {
                    Id: customerRegistrationInfo.organizationId,
                    UniqueName: customerRegistrationInfo.organizationName,
                    IsProduction: customerRegistrationInfo.isProduction,
                    EnvironmentUrl: environmentUrl,
                    PlatformType: CustomerInfoStore.DefaultPlatformType
                }
            ]
        };
        console.log(`sending: `, record);

        const url = customerRegistrationInfo.registerAction == RegisterAction.Register
            ? `/api/customer/register/`
            : `/api/customer/update/${customerRegistrationInfo.id.replace("customers/", "")}`;

        const desc = customerRegistrationInfo.registerAction == RegisterAction.Register
            ? 'registering customer'
            : 'updating customer';

        // Use registerOrganization both for register or update actions
        await this.registerOrganization(customerRegistrationInfo.organizationId, url, record, desc);
    }

    private async registerOrganization(organizationId: string, url: string, record: any, description: string) {

        let response: Response;
        try {
            response = await this.http.fetch(url, {
                method: 'post',
                body: json(record)
            });

        } catch (error) {
            // If error is a 409 conflict, rethrow error for custom handling in register-customer.ts
            if (error.status === 409) {
                console.log("HTTP 409 conflict error received: " + error.message);
                throw error;
            }

            const formattedError = `Error ${description}: ${error.statusText}`;
            console.log(formattedError, error);
            throw new Error(formattedError);
        }

        if (response.ok === false) {
            const errorBody = await response.text();
            const formattedError = `Error ${description}: ${errorBody}`;
            console.error(formattedError, response, errorBody);
            throw new Error(formattedError);
        }

        let customerInfo: CustomerRegistrationInfo;
        try {
            customerInfo = await response.json();
        } catch (e) {
            const formattedError = `Error parsing result while ${description}: ${e}`;
            console.error(formattedError, e);
            throw new Error(formattedError)
        }
        customerInfo.status = "registered";

        console.log(`${description} success (id=${customerInfo.id}).`);

        this.updateCustomer(organizationId, customerInfo);
    }

    private async retrieveOrganization(organizationId: string): Promise<CustomerRegistrationInfo> {

        // Gets organizationinfo from the licenseserver
        return this.http.fetch("/api/OrganizationInfo/" + organizationId)
            .then(response => response.json())
            .then((customerInfo: CustomerRegistrationInfo) => {
                const customerId = customerInfo.id;
                customerInfo.status = "registered";
                console.log(`Customer retrieved: `, customerInfo.id);
                return customerInfo;
            })
            .catch((error: any): CustomerRegistrationInfo => {
                console.log("Error while retrieving organization info", error);

                if (error.status === 404) {
                    return {
                        status: "notregistered",
                        id: null,
                        company: null,
                        name: null,
                        email: null,
                        contractLines: [],
                        organizations: [],
                        gdprConsentGiven: false,
                        signedUpForNewsLetter: false
                    }
                    // Unknown organization, navigate to register page
                }
                else {
                    this.router
                        .navigateToRoute('error', { errorMessage: `An error occurred while loading this page, details: ${error.statusText}` });
                    
                    return {
                        status: "notregistered",
                        id: null,
                        company: null,
                        name: null,
                        email: null,
                        contractLines: [],
                        organizations: [],
                        gdprConsentGiven: false,
                        signedUpForNewsLetter: false
                    }
                }
            });
    }

    private updateCustomer(organizationId: string, customerInfo: CustomerRegistrationInfo) {
        const customer = this.getCustomerInfo(organizationId, null);
        customer.company = customerInfo.company;
        customer.id = customerInfo.id;
        customer.name = customerInfo.name;
        customer.email = customerInfo.email;
        customer.gdprConsentGiven = customerInfo.gdprConsentGiven;
        customer.signedUpForNewsLetter = customerInfo.signedUpForNewsLetter;
        customer.contractLines = customerInfo.contractLines;
        customer.organizations = customerInfo.organizations;
        customer.status = customerInfo.status;
    }
}



// WEBPACK FOOTER //
// ./ClientApp/app/services/customer-info-store.ts